import React from 'react';
import PropTypes from 'prop-types';
import { useDragLayer } from 'react-dnd';

import styles from './DragPreview.module.scss';

const propTypes = {

	/**
	 * The valid item types to accept for the drag preview.
	 */
	accept: PropTypes.arrayOf(PropTypes.string).isRequired,

	/**
	 * The children to render the preview for the item being dragged.
	 */
	children: PropTypes.node.isRequired,

};

const defaultProps = {
	accept: [],
};

/**
 * Positions the drag preview item on the screen to match the drag location.
 *
 * @param   {object} initialOffset  The initial mouse position of the drag.
 * @param   {object} currentOffset  The current mouse position of the drag.
 * @returns {object}                The style required to position the drag preview item.
 */
const positionDragPreview = (initialOffset, currentOffset) => {

	if (!initialOffset || !currentOffset) {
		return {
			display: 'none',
		};
	}

	// eslint-disable-next-line
	const { x, y } = currentOffset;

	const transform = `translate(${x}px, ${y}px)`;

	return {
		transform,
		WebkitTransform: transform,
	};

};

/**
 * Shared component to render a Drag Preview, using react-dnd.
 *
 * @param   {Object} props  The props.
 * @returns {Object}        The rendered component.
 */
const DragPreview = ({ accept, children }) => {

	const { item, initialOffset, currentOffset } = useDragLayer((monitor) => ({
		item: monitor.getItem(),
		initialOffset: monitor.getInitialSourceClientOffset(),
		currentOffset: monitor.getSourceClientOffset(),
	}));

	if (!item || !item.type || !item.data || accept.indexOf(item.type) === -1) {
		return null;
	}

	return (
		<div
			style={{
				position: 'fixed',
				pointerEvents: 'none',
				zIndex: 100,
				left: 0,
				top: 0,
			}}>
			<div style={positionDragPreview(initialOffset, currentOffset)}>
				<div className={styles['drag-preview-item']}>
					<span className={styles['drag-preview-icon']}>
						<i className="fas fa-grip-vertical"></i>
					</span>
					<div className={styles['drag-preview-content-container']}>
						{React.cloneElement(children, { item })}
					</div>
				</div>
			</div>
		</div>
	);
};

DragPreview.propTypes = propTypes;
DragPreview.defaultProps = defaultProps;

export default DragPreview;
